import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { AtiraSelect } from "../../../../../components/AtiraSelect";
import { AtiraTabs } from "../../../../../components/AtiraTabs";
import { Button } from "../../../../../components/Button";
import { Flex } from "../../../../../components/Flex";
import { IconSelectModal } from "../../../../../components/IconSelectModal";
import { ImageUpload } from "../../../../../components/ImageUpload";
import { Input } from "../../../../../components/Input";
import { Text } from "../../../../../components/Text";
import { Textarea } from "../../../../../components/Textarea";
import { AdminUpdateRoadmapItemDto } from "../../../../../model/admin/dto/AdminUpdateRoadmapItemDto";
import { RoadmapItem } from "../../../../../model/roadmap/RoadmapItem";
import { RoadmapItemStatus } from "../../../../../model/roadmap/types/RoadmapItemStatus.enum";
import { AppLangs } from "../../../../../model/shared/enum/AppLangs.enum";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { GenericIcon } from "../../../../../model/shared/types/GenericIcon";
import {
  LocalizedList,
  LocalizedText,
} from "../../../../../model/shared/types/LocalizedField";
import { adminActions } from "../../../../../redux/admin/admin.slice";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { Spacing } from "../../../../../theme/Spacing";
import { TestIDs } from "../../../../../tests/types/testIds";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { getFontAwesomeIcon } from "../../../../../utils/getFontAwesomeIcon";
import type { UploadFile } from "../../../../../utils/local-antd";
import { Drawer } from "../../../../../utils/local-antd";
import { onSetTypedCRUDErrors } from "../../../../../utils/onSetTypedCRUDErrors";
import { DrawerDefaultsExtra } from "../../../../tasks/components/DrawerDefaultsExtra";

const StyledInput = styled(Input)`
  height: 2.5rem;
  border: 2px solid ${(props) => props.theme.main};
`;

const ImageUploadWrapper = styled(Flex)`
  & > div {
    width: 50%;
  }
`;

const IconsButton = styled(Button)`
  width: fit-content;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  border: ${({ theme }) => `1px solid ${theme.main}`};
  margin: 0;
  gap: ${Spacing.s};
`;

const StyledTabs = styled(AtiraTabs)`
  padding-inline: 0;

  .ant-tabs-nav-list {
    width: 100% !important;
  }

  .ant-tabs-tab {
    flex-grow: 1;
    justify-content: center;
  }
`;

const LANGS = [AppLangs.EN, AppLangs.AR, AppLangs.TR];

/**
 * Bullet points are edited as one textarea per language (one bullet per line)
 * and converted to/from the `string[]` the API stores.
 */
type RoadmapItemFormValues = {
  title: Record<AppLangs, string>;
  descriptions: Record<AppLangs, string>;
  status: RoadmapItemStatus;
  period?: string;
  icon?: GenericIcon;
  images?: (string | UploadFile | File | null)[];
  order?: number;
};

const toFormValues = (
  roadmapItem?: RoadmapItem | null,
): RoadmapItemFormValues => {
  const title = {} as Record<AppLangs, string>;
  const descriptions = {} as Record<AppLangs, string>;

  LANGS.forEach((lang) => {
    title[lang] = roadmapItem?.title?.[lang] || "";
    descriptions[lang] = (roadmapItem?.descriptions?.[lang] || []).join("\n");
  });

  return {
    title,
    descriptions,
    status: roadmapItem?.status || RoadmapItemStatus.UPCOMING,
    period: roadmapItem?.period || "",
    icon: roadmapItem?.icon,
    images: roadmapItem?.images || [],
    order: roadmapItem?.order ?? 0,
  };
};

const toDto = (values: RoadmapItemFormValues) => {
  const title = { [AppLangs.EN]: "" } as LocalizedText;
  const descriptions = { [AppLangs.EN]: [] } as LocalizedList;

  LANGS.forEach((lang) => {
    const localizedTitle = values.title?.[lang]?.trim();

    // empty translations are dropped so the website falls back to English
    if (localizedTitle) {
      title[lang] = localizedTitle;
    }

    const bullets = (values.descriptions?.[lang] || "")
      .split("\n")
      .map((bullet) => bullet.trim())
      .filter(Boolean);

    if (bullets.length) {
      descriptions[lang] = bullets;
    }
  });

  return {
    title,
    descriptions,
    status: values.status,
    // sent as an empty string / null rather than `undefined` so clearing the
    // field in the drawer actually clears it server-side
    period: values.period?.trim() || "",
    icon: values.icon || null,
    images: values.images as string[],
    order: Number(values.order) || 0,
  };
};

type AdminRoadmapItemUpdateDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  roadmapItem: RoadmapItem | null;
};

export const AdminRoadmapItemUpdateDrawer: React.FC<
  AdminRoadmapItemUpdateDrawerProps
> = ({ open = false, onClose, roadmapItem }) => {
  const [loading, setLoading] = useState(false);
  const [iconsModalVisible, setIconsModalVisible] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { control, getValues, setValue, reset, handleSubmit, watch, setError } =
    useForm<RoadmapItemFormValues>();

  const icon = watch("icon");
  const selectedIcon = getFontAwesomeIcon(icon as GenericIcon);

  const onUpdate = async () => {
    try {
      setLoading(true);

      await dispatch(
        adminActions.updateRoadmapItem({
          ...toDto(getValues()),
          roadmapItemId: roadmapItem!._id,
          userId,
        } as AdminUpdateRoadmapItemDto),
      ).unwrap();
      await dispatch(adminActions.getRoadmapItems({ userId })).unwrap();

      localOnClose();
      AtiraToast.success(t("admin.roadmap.drawer.update.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    reset(toFormValues(roadmapItem));
  }, [reset, roadmapItem]);

  const localOnClose = () => {
    onClose();
    resetForm();
  };

  useEffect(() => {
    if (open && roadmapItem) {
      resetForm();
    }
  }, [open, resetForm, roadmapItem]);

  return (
    <Drawer
      title={t("admin.roadmap.drawer.update.title")}
      open={open}
      onClose={localOnClose}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onUpdate)}
          onCancel={localOnClose}
          config={{ loading }}
          submitTestId={TestIDs.roadmap.createEdit.submit}
          cancelTestId={TestIDs.roadmap.createEdit.cancel}
        />
      }
      maskClosable={false}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        <StyledTabs
          items={LANGS.map((lang) => ({
            key: lang,
            label: (
              <span data-testid={TestIDs.roadmap.createEdit.lang(lang)}>
                {t(`common.lang.${lang}`)}
              </span>
            ),
            icon: <></>,
            children: (
              <Flex flexDirection="column" gap="m" paddingTop="m">
                <Controller
                  control={control}
                  name={`title.${lang}` as const}
                  rules={{
                    required: {
                      // only English is mandatory, the rest fall back to it
                      value: lang === AppLangs.EN,
                      message: t("common.this_field_required"),
                    },
                    maxLength: {
                      value: Lengths.LONG,
                      message: t("error.length.less", {
                        name: t("common.title"),
                        length: Lengths.LONG,
                      }),
                    },
                  }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <StyledInput
                      value={value}
                      onChange={onChange}
                      title={t("common.title")}
                      required={lang === AppLangs.EN}
                      valid={!error}
                      errorMessage={error?.message}
                      maxLength={Lengths.LONG}
                      dataTestId={TestIDs.roadmap.createEdit.title(lang)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`descriptions.${lang}` as const}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Textarea
                      value={value}
                      onChange={onChange}
                      title={t("admin.roadmap.drawer.bullets.title")}
                      info={t("admin.roadmap.drawer.bullets.info")}
                      placeholder={t("admin.roadmap.drawer.bullets.placeholder")}
                      rows={6}
                      valid={!error}
                      errorMessage={error?.message}
                      dataTestId={TestIDs.roadmap.createEdit.descriptions(lang)}
                    />
                  )}
                />
              </Flex>
            ),
          }))}
        />

        <Controller
          control={control}
          name="status"
          rules={{
            required: {
              value: true,
              message: t("common.this_field_required"),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraSelect
              title={t("admin.roadmap.drawer.status.title")}
              value={value}
              onChange={onChange}
              required
              valid={!error}
              errorMessage={error?.message}
              className={TestIDs.roadmap.createEdit.status.open}
              options={Object.values(RoadmapItemStatus).map((status) => ({
                label: t(`admin.roadmap.status.${status.toLowerCase()}`),
                value: status,
                "data-testid": TestIDs.roadmap.createEdit.status.option(status),
              }))}
            />
          )}
        />

        <Flex gap="m" flexWrap="wrap">
          <Controller
            control={control}
            name="period"
            rules={{
              maxLength: {
                value: Lengths.NAME,
                message: t("error.length.less", {
                  name: t("admin.roadmap.drawer.period.title"),
                  length: Lengths.NAME,
                }),
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <StyledInput
                value={value}
                onChange={onChange}
                title={t("admin.roadmap.drawer.period.title")}
                info={t("admin.roadmap.drawer.period.info")}
                placeholder="Q3 2026"
                valid={!error}
                errorMessage={error?.message}
                maxLength={Lengths.NAME}
                width="48%"
                dataTestId={TestIDs.roadmap.createEdit.period}
              />
            )}
          />

          <Controller
            control={control}
            name="order"
            render={({ field: { value, onChange } }) => (
              <StyledInput
                type="number"
                value={value}
                onChange={onChange}
                title={t("admin.roadmap.drawer.order.title")}
                info={t("admin.roadmap.drawer.order.info")}
                width="48%"
                dataTestId={TestIDs.roadmap.createEdit.order}
              />
            )}
          />
        </Flex>

        <Flex flexDirection="column" gap="s">
          <Text>{t("admin.roadmap.drawer.icon.title")}</Text>

          <Flex alignItems="center" gap="m">
            {selectedIcon ? (
              <AtiraIcon
                icon={selectedIcon as IconProp}
                color="main"
                size="xl"
              />
            ) : null}

            <IconsButton
              onClick={() => setIconsModalVisible(true)}
              icon={faAngleDown}
              dataTestId={TestIDs.roadmap.createEdit.icon.button}
            >
              {t("modules.definition.basicInfo.icon.button")}
            </IconsButton>

            {icon ? (
              <Button
                onClick={() => setValue("icon", undefined)}
                margin="none"
                backgroundColor="transparent"
                color="danger"
                dataTestId={TestIDs.roadmap.createEdit.icon.remove}
              >
                {t("common.remove")}
              </Button>
            ) : null}
          </Flex>
        </Flex>

        <Controller
          control={control}
          name={`images.${0}` as const}
          render={({ field: { value, onChange } }) => (
            <ImageUploadWrapper>
              <ImageUpload
                onChange={(f) => onChange(f || null)}
                image={(value as string) || ""}
                title={t("common.image")}
                showAspectRatioTooltip={false}
                listType="picture-card"
              />
            </ImageUploadWrapper>
          )}
        />
      </Flex>

      <IconSelectModal
        open={iconsModalVisible}
        onClose={() => setIconsModalVisible(false)}
        onChange={(value) => setValue("icon", value)}
        currentIcon={icon as GenericIcon}
      />
    </Drawer>
  );
};
